import localforage from "localforage";
import {getFromCms} from "@utils/axios";
import {toSearchable} from "@utils/searching";
import {
    PATHISIS_CATEGORIES,
    PATHISIS_ALL_ITEMS,
    PATHISIS_SEARCH_INDEX,
    PATHISIS_LAST_UPDATE
} from "@utils/storageKeys";

const CONTENT_FIELDS = [
    'sustaseis_diatrofis',
    'odigies_diaitomenou',
    'proteinomena_simpliromata',
    'diagnwstika_ergaleia'
];

const SEARCH_INDEX_VERSION = CONTENT_FIELDS.join(',');

const WEIGHT_TITLE = 10;
const WEIGHT_CONTENT = 2;

function stripMarkdown(text) {
    return text
        .replace(/[#*_~`>\[\]()!|\\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function buildSearchIndex(allItems) {
    return allItems.map(item => ({
        id: item.id,
        titleKey: toSearchable(item.title || ''),
        contentKeys: CONTENT_FIELDS
            .map(f => item[f] ? toSearchable(stripMarkdown(item[f])) : '')
            .filter(Boolean)
    }));
}

/**
 * Checks if the cache has expired (24h) and clears all pathisis data if so.
 * @returns {boolean} true if cache was invalidated
 */
export async function checkIfUpdateRequired() {
    const lastUpdate = await localforage.getItem(PATHISIS_LAST_UPDATE);
    if (lastUpdate) {
        const expiresAt = new Date(lastUpdate);
        expiresAt.setDate(expiresAt.getDate() + 1);
        if (new Date() > expiresAt) {
            await Promise.all([
                localforage.removeItem(PATHISIS_CATEGORIES),
                localforage.removeItem(PATHISIS_ALL_ITEMS),
                localforage.removeItem(PATHISIS_SEARCH_INDEX),
            ]);
            await localforage.setItem(PATHISIS_LAST_UPDATE, new Date().toISOString());
            return true;
        }
    } else {
        await localforage.setItem(PATHISIS_LAST_UPDATE, new Date().toISOString());
    }
    return false;
}

export async function fetchCategories() {
    const cached = await localforage.getItem(PATHISIS_CATEGORIES);
    if (cached && cached.length > 0) return cached;

    const res = await getFromCms('https://cms.athlisis.com/pathisis-categories');
    await localforage.setItem(PATHISIS_CATEGORIES, res.data);
    return res.data;
}

/**
 * Fetches all pathisis items via pagination, enriches them with category info,
 * and builds the search index. Returns cached data when valid.
 * @param {Array} categories
 * @returns {{allItems: Array, searchIndex: Array}}
 */
export async function fetchAllItems(categories) {
    const cached = await localforage.getItem(PATHISIS_ALL_ITEMS);
    const cachedIndex = await localforage.getItem(PATHISIS_SEARCH_INDEX);

    if (cached && cachedIndex && cachedIndex._v === SEARCH_INDEX_VERSION) {
        return {allItems: cached, searchIndex: cachedIndex.data};
    }

    let allItems = cached;

    if (!allItems) {
        const categoryMap = {};
        for (const cat of categories) {
            categoryMap[cat.id] = cat.name;
        }

        const {data} = await getFromCms('https://cms.athlisis.com/pathises', {
            _limit: -1,
            _sort: 'title:ASC'
        });

        allItems = (data || []).map(item => {
            const catId = item.pathisis_category?.id || item.pathisis_category;
            return {
                ...item,
                _categoryId: catId,
                _categoryName: categoryMap[catId] || ''
            };
        });

        await localforage.setItem(PATHISIS_ALL_ITEMS, allItems);
    }

    const searchIndex = buildSearchIndex(allItems);
    await localforage.setItem(PATHISIS_SEARCH_INDEX, {_v: SEARCH_INDEX_VERSION, data: searchIndex});

    return {allItems, searchIndex};
}

/**
 * Filters allItems by category ID (client-side).
 */
export function getItemsByCategory(allItems, categoryId) {
    return allItems.filter(item => item._categoryId == categoryId);
}

/**
 * Performs a weighted search across the pre-built index.
 * Title matches score higher than content matches.
 * @param {string} query
 * @param {Array} searchIndex
 * @param {Array} allItems
 * @returns {Array} sorted results
 */
export function search(query, searchIndex, allItems) {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const searchKey = toSearchable(trimmed);
    const scored = [];

    for (let i = 0; i < searchIndex.length; i++) {
        const idx = searchIndex[i];
        let score = 0;

        if (idx.titleKey.includes(searchKey)) {
            score += WEIGHT_TITLE;
        }

        for (const ck of idx.contentKeys) {
            if (ck.includes(searchKey)) {
                score += WEIGHT_CONTENT;
                break;
            }
        }

        if (score > 0) {
            scored.push({item: allItems[i], score});
        }
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.map(s => s.item);
}
